import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Users, 
  Building2, 
  Plus, 
  Trash2, 
  Edit, 
  MoreVertical,
  Mail,
  Phone,
  FileText,
  Eye,
  ChevronDown,
  ChevronRight,
  UserPlus,
  Shield,
  RefreshCw,
  Search,
  X,
  Save,
  MapPin,
  Pencil
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { Navigation } from "../components/Navigation";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const UsersManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [franchises, setFranchises] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [expandedFranchises, setExpandedFranchises] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showCreateFranchise, setShowCreateFranchise] = useState(false);
  const [showCreateCollaborator, setShowCreateCollaborator] = useState(false);
  const [showEditCollaborator, setShowEditCollaborator] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form states
  const [franchiseForm, setFranchiseForm] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: '',
    office_address: ''
  });
  const [collaboratorForm, setCollaboratorForm] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    office_address: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    office_address: ''
  });

  // Agency state
  const [agencyForm, setAgencyForm] = useState({
    agency_name: '',
    agency_address: '',
    agency_city: ''
  });
  const [editingAgency, setEditingAgency] = useState(false);
  const [savingAgency, setSavingAgency] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'franchise_owner') {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFranchises(response.data.franchises || []);
        setAdmins(response.data.admins || []);
        
        // Auto-expand all franchises
        const expanded = {};
        (response.data.franchises || []).forEach(f => {
          expanded[f.id] = true;
        });
        setExpandedFranchises(expanded);
        
        // Load agency info for franchise owners
        if (user?.role === 'franchise_owner') {
          const myFranchise = (response.data.franchises || [])[0];
          if (myFranchise) {
            setAgencyForm({
              agency_name: myFranchise.agency_name || '',
              agency_address: myFranchise.agency_address || '',
              agency_city: myFranchise.agency_city || ''
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFranchise = async () => {
    if (!franchiseForm.email || !franchiseForm.password || !franchiseForm.name || !franchiseForm.company_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      setFormLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_URL}/api/users/franchise`,
        franchiseForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Franchise créée avec succès');
        setShowCreateFranchise(false);
        setFranchiseForm({ email: '', password: '', name: '', company_name: '', phone: '', office_address: '' });
        fetchUsers();
      }
    } catch (error) {
      console.error('Error creating franchise:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateCollaborator = async () => {
    if (!collaboratorForm.email || !collaboratorForm.password || !collaboratorForm.name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      setFormLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_URL}/api/users/collaborator`,
        {
          ...collaboratorForm,
          franchise_id: selectedFranchise?.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Collaborateur créé avec succès');
        setShowCreateCollaborator(false);
        setCollaboratorForm({ email: '', password: '', name: '', phone: '', office_address: '' });
        setSelectedFranchise(null);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error creating collaborator:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = isActive 
        ? `${API_URL}/api/users/${userId}`
        : `${API_URL}/api/users/${userId}/reactivate`;
      
      if (isActive) {
        await axios.delete(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Utilisateur désactivé');
      } else {
        await axios.put(endpoint, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Utilisateur réactivé');
      }
      
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleEditCollaborator = async () => {
    if (!editForm.name) {
      toast.error('Le nom est obligatoire');
      return;
    }
    
    try {
      setFormLoading(true);
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${API_URL}/api/users/${selectedCollaborator.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Collaborateur modifié avec succès');
      setShowEditCollaborator(false);
      setSelectedCollaborator(null);
      setEditForm({ name: '', phone: '', office_address: '' });
      fetchUsers();
    } catch (error) {
      console.error('Error editing collaborator:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteCollaborator = async () => {
    try {
      setFormLoading(true);
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${API_URL}/api/users/${selectedCollaborator.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Collaborateur supprimé avec succès');
      setShowDeleteConfirm(false);
      setSelectedCollaborator(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting collaborator:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (collab) => {
    setSelectedCollaborator(collab);
    setEditForm({
      name: collab.name || '',
      phone: collab.phone || '',
      office_address: collab.office_address || ''
    });
    setShowEditCollaborator(true);
  };

  const handleSaveAgency = async () => {
    setSavingAgency(true);
    try {
      const token = localStorage.getItem('auth_token');
      const myFranchise = franchises[0];
      if (!myFranchise) return;
      
      await axios.put(
        `${API_URL}/api/users/${myFranchise.id}`,
        agencyForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Informations agence mises à jour');
      setEditingAgency(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving agency:', error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingAgency(false);
    }
  };

  const openDeleteDialog = (collab) => {
    setSelectedCollaborator(collab);
    setShowDeleteConfirm(true);
  };

  const toggleFranchiseExpand = (franchiseId) => {
    setExpandedFranchises(prev => ({
      ...prev,
      [franchiseId]: !prev[franchiseId]
    }));
  };

  const filteredFranchises = franchises.filter(franchise => {
    const searchLower = searchTerm.toLowerCase();
    const matchesFranchise = 
      franchise.company_name?.toLowerCase().includes(searchLower) ||
      franchise.name?.toLowerCase().includes(searchLower) ||
      franchise.email?.toLowerCase().includes(searchLower);
    
    const matchesCollaborator = franchise.collaborators?.some(collab =>
      collab.name?.toLowerCase().includes(searchLower) ||
      collab.email?.toLowerCase().includes(searchLower)
    );
    
    return matchesFranchise || matchesCollaborator;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {user?.role === 'admin' ? 'Gestion des Franchises' : 'Mon Équipe'}
            </h1>
            <p className="text-gray-500 mt-1">
              {user?.role === 'admin' 
                ? 'Gérez vos franchisés et leurs collaborateurs'
                : 'Gérez votre équipe et vos collaborateurs'}
            </p>
          </div>
          
          {user?.role === 'admin' && (
            <Button 
              onClick={() => setShowCreateFranchise(true)}
              className="bg-[#397DFF] hover:bg-[#2a6ae8]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Franchise
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className={`grid grid-cols-1 ${user?.role === 'admin' ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4 mb-8`}>
          {user?.role === 'admin' && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{franchises.length}</p>
                    <p className="text-sm text-gray-500">Franchises</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {franchises.reduce((acc, f) => acc + (f.collaborators?.length || 0), 0)}
                  </p>
                  <p className="text-sm text-gray-500">Collaborateurs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {franchises.reduce((acc, f) => acc + (f.report_count || 0), 0)}
                  </p>
                  <p className="text-sm text-gray-500">Rapports totaux</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search - Only for admin */}
        {user?.role === 'admin' && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
            placeholder="Rechercher une franchise ou un collaborateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
          </div>
        )}

        {/* Mon Agence - Only for franchise owners */}
        {user?.role === 'franchise_owner' && (
          <Card className="mb-6 border-[#397DFF]/20">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#397DFF]" />
                  <CardTitle className="text-lg">Mon Agence</CardTitle>
                </div>
                {!editingAgency ? (
                  <Button variant="ghost" size="sm" onClick={() => setEditingAgency(true)}>
                    <Pencil className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingAgency(false)}>Annuler</Button>
                    <Button size="sm" onClick={handleSaveAgency} disabled={savingAgency} className="bg-[#397DFF] hover:bg-[#2a6ae8]">
                      <Save className="w-4 h-4 mr-1" /> {savingAgency ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </div>
                )}
              </div>
              <CardDescription>Informations affichées dans vos rapports PDF</CardDescription>
            </CardHeader>
            <CardContent>
              {editingAgency ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="agency_name">Nom de l'agence</Label>
                    <Input
                      id="agency_name"
                      placeholder="Agence Dunkerque - Flandres"
                      value={agencyForm.agency_name}
                      onChange={(e) => setAgencyForm(prev => ({ ...prev, agency_name: e.target.value }))}
                      data-testid="agency-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="agency_address">Adresse</Label>
                    <Input
                      id="agency_address"
                      placeholder="1 Bd Sainte-Barbe"
                      value={agencyForm.agency_address}
                      onChange={(e) => setAgencyForm(prev => ({ ...prev, agency_address: e.target.value }))}
                      data-testid="agency-address-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="agency_city">Ville</Label>
                    <Input
                      id="agency_city"
                      placeholder="59140 Dunkerque"
                      value={agencyForm.agency_city}
                      onChange={(e) => setAgencyForm(prev => ({ ...prev, agency_city: e.target.value }))}
                      data-testid="agency-city-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#397DFF]/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#397DFF]" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{agencyForm.agency_name || 'Non renseignée'}</p>
                    {(agencyForm.agency_address || agencyForm.agency_city) ? (
                      <p className="text-sm text-muted-foreground">{agencyForm.agency_address}{agencyForm.agency_city ? `, ${agencyForm.agency_city}` : ''}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Cliquez sur "Modifier" pour renseigner votre agence</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Franchises List */}
        <div className="space-y-4">
          {filteredFranchises.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  {user?.role === 'admin' ? 'Aucune franchise trouvée' : 'Aucun collaborateur dans votre équipe'}
                </p>
                {user?.role === 'admin' && (
                  <Button 
                    variant="link" 
                    onClick={() => setShowCreateFranchise(true)}
                    className="mt-2"
                  >
                    Créer une première franchise
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredFranchises.map((franchise) => (
              <Card key={franchise.id} className="overflow-hidden">
                {/* Franchise Header - Always expanded for franchise_owner viewing their own */}
                <div 
                  className={`flex items-center justify-between p-4 bg-gradient-to-r from-[#397DFF]/5 to-transparent ${user?.role === 'admin' ? 'cursor-pointer hover:from-[#397DFF]/10' : ''} transition-colors`}
                  onClick={() => user?.role === 'admin' && toggleFranchiseExpand(franchise.id)}
                >
                  <div className="flex items-center gap-4">
                    {user?.role === 'admin' && (
                      <button className="p-1">
                        {expandedFranchises[franchise.id] ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                    )}
                    
                    <div className="p-2 bg-[#397DFF] rounded-lg">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">
                          {franchise.company_name}
                        </h3>
                        {!franchise.is_active && (
                          <Badge variant="destructive" className="text-xs">Inactif</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{franchise.name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden md:block">
                      <p className="text-sm font-medium">{franchise.report_count || 0} rapports</p>
                      <p className="text-xs text-gray-500">
                        {franchise.collaborators?.length || 0} collaborateur{(franchise.collaborators?.length || 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                    
                    {/* Dropdown menu - shown for admin, or for franchise viewing their own */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFranchise(franchise);
                          setShowCreateCollaborator(true);
                        }}>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Ajouter collaborateur
                        </DropdownMenuItem>
                        {user?.role === 'admin' && (
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            handleToggleUserStatus(franchise.id, franchise.is_active);
                          }}>
                            {franchise.is_active ? (
                              <>
                                <Trash2 className="w-4 h-4 mr-2" />
                                Désactiver
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Réactiver
                              </>
                            )}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                {/* Franchise Details & Collaborators - Always show for franchise_owner, toggle for admin */}
                {(user?.role === 'franchise_owner' || expandedFranchises[franchise.id]) && (
                  <div className="border-t">
                    {/* Contact Info */}
                    <div className="p-4 bg-gray-50 flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        {franchise.email}
                      </div>
                      {franchise.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          {franchise.phone}
                        </div>
                      )}
                    </div>
                    
                    {/* Collaborators */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">Collaborateurs</h4>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedFranchise(franchise);
                            setShowCreateCollaborator(true);
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Ajouter
                        </Button>
                      </div>
                      
                      {franchise.collaborators?.length > 0 ? (
                        <div className="space-y-2">
                          {franchise.collaborators.map((collab) => (
                            <div 
                              key={collab.id}
                              className="flex items-center justify-between p-3 bg-white border rounded-lg"
                              data-testid={`collaborator-item-${collab.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                  <Users className="w-4 h-4 text-gray-500" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{collab.name}</p>
                                  <p className="text-xs text-gray-500">{collab.email}</p>
                                  {collab.phone && (
                                    <p className="text-xs text-gray-400">{collab.phone}</p>
                                  )}
                                  {collab.office_address && (
                                    <p className="text-xs text-gray-400">{collab.office_address}</p>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {!collab.is_active && (
                                  <Badge variant="secondary" className="text-xs">Inactif</Badge>
                                )}
                                {collab.is_active ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openEditDialog(collab)}
                                      title="Modifier"
                                      data-testid={`edit-collaborator-${collab.id}`}
                                    >
                                      <Edit className="w-4 h-4 text-gray-400 hover:text-blue-500" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openDeleteDialog(collab)}
                                      title="Supprimer"
                                      data-testid={`delete-collaborator-${collab.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleUserStatus(collab.id, collab.is_active)}
                                    title="Réactiver"
                                  >
                                    <RefreshCw className="w-4 h-4 text-gray-400 hover:text-green-500" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-4">
                          Aucun collaborateur
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create Franchise Dialog */}
      <Dialog open={showCreateFranchise} onOpenChange={setShowCreateFranchise}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle Franchise</DialogTitle>
            <DialogDescription>
              Créez un compte pour un nouveau franchisé
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Nom de l'entreprise *</Label>
              <Input
                id="company_name"
                placeholder="Ex: LOGEA"
                value={franchiseForm.company_name}
                onChange={(e) => setFranchiseForm({...franchiseForm, company_name: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Nom du responsable *</Label>
              <Input
                id="name"
                placeholder="Ex: Jean Dupont"
                value={franchiseForm.name}
                onChange={(e) => setFranchiseForm({...franchiseForm, name: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="contact@logea.fr"
                value={franchiseForm.email}
                onChange={(e) => setFranchiseForm({...franchiseForm, email: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Minimum 8 caractères"
                value={franchiseForm.password}
                onChange={(e) => setFranchiseForm({...franchiseForm, password: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                placeholder="06 12 34 56 78"
                value={franchiseForm.phone}
                onChange={(e) => setFranchiseForm({...franchiseForm, phone: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="office_address">Adresse bureau</Label>
              <Input
                id="office_address"
                placeholder="1 Bd Sainte-Barbe, 59140 Dunkerque"
                value={franchiseForm.office_address}
                onChange={(e) => setFranchiseForm({...franchiseForm, office_address: e.target.value})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateFranchise(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateFranchise}
              disabled={formLoading}
              className="bg-[#397DFF] hover:bg-[#2a6ae8]"
            >
              {formLoading ? 'Création...' : 'Créer la franchise'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Collaborator Dialog */}
      <Dialog open={showCreateCollaborator} onOpenChange={setShowCreateCollaborator}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau Collaborateur</DialogTitle>
            <DialogDescription>
              Ajoutez un collaborateur à {selectedFranchise?.company_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="collab_name">Nom *</Label>
              <Input
                id="collab_name"
                placeholder="Ex: Marie Martin"
                value={collaboratorForm.name}
                onChange={(e) => setCollaboratorForm({...collaboratorForm, name: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="collab_email">Email *</Label>
              <Input
                id="collab_email"
                type="email"
                placeholder="marie.martin@logea.fr"
                value={collaboratorForm.email}
                onChange={(e) => setCollaboratorForm({...collaboratorForm, email: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="collab_password">Mot de passe *</Label>
              <Input
                id="collab_password"
                type="password"
                placeholder="Minimum 8 caractères"
                value={collaboratorForm.password}
                onChange={(e) => setCollaboratorForm({...collaboratorForm, password: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="collab_phone">Téléphone</Label>
              <Input
                id="collab_phone"
                placeholder="06 12 34 56 78"
                value={collaboratorForm.phone}
                onChange={(e) => setCollaboratorForm({...collaboratorForm, phone: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="collab_address">Adresse bureau</Label>
              <Input
                id="collab_address"
                placeholder="1 Bd Sainte-Barbe, 59140 Dunkerque"
                value={collaboratorForm.office_address}
                onChange={(e) => setCollaboratorForm({...collaboratorForm, office_address: e.target.value})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateCollaborator(false);
              setSelectedFranchise(null);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateCollaborator}
              disabled={formLoading}
              className="bg-[#397DFF] hover:bg-[#2a6ae8]"
            >
              {formLoading ? 'Création...' : 'Créer le collaborateur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Collaborator Dialog */}
      <Dialog open={showEditCollaborator} onOpenChange={(open) => {
        setShowEditCollaborator(open);
        if (!open) {
          setSelectedCollaborator(null);
          setEditForm({ name: '', phone: '', office_address: '' });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le collaborateur</DialogTitle>
            <DialogDescription>
              Modifiez les informations de {selectedCollaborator?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Nom *</Label>
              <Input
                id="edit_name"
                placeholder="Ex: Marie Martin"
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                data-testid="edit-collaborator-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_phone">Téléphone</Label>
              <Input
                id="edit_phone"
                placeholder="06 12 34 56 78"
                value={editForm.phone}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                data-testid="edit-collaborator-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_address">Adresse bureau</Label>
              <Input
                id="edit_address"
                placeholder="1 Bd Sainte-Barbe, 59140 Dunkerque"
                value={editForm.office_address}
                onChange={(e) => setEditForm({...editForm, office_address: e.target.value})}
                data-testid="edit-collaborator-address"
              />
            </div>

            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
              <p className="font-medium">Email : {selectedCollaborator?.email}</p>
              <p className="text-xs mt-1">(L'email ne peut pas être modifié)</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditCollaborator(false);
              setSelectedCollaborator(null);
              setEditForm({ name: '', phone: '', office_address: '' });
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleEditCollaborator}
              disabled={formLoading}
              className="bg-[#397DFF] hover:bg-[#2a6ae8]"
              data-testid="save-edit-collaborator"
            >
              {formLoading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => {
        setShowDeleteConfirm(open);
        if (!open) setSelectedCollaborator(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer le collaborateur
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{selectedCollaborator?.name}</strong> ?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                Cette action est irréversible. Le collaborateur ne pourra plus accéder à la plateforme.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setSelectedCollaborator(null);
            }}>
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteCollaborator}
              disabled={formLoading}
              data-testid="confirm-delete-collaborator"
            >
              {formLoading ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagementPage;
